import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { BookOpen, ChevronRight, Sparkles } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { HOME_STRINGS } from '../lib/useHomeLang';

/**
 * StarterLibrarySection — Iter-52.16 compact preview.
 *
 * Home page card. Renders ONE English + ONE Hindi preview card at
 * the CHILD'S SUGGESTED LEVEL (derived from their class_level on the
 * profile) so the passage they see first is already reading-age-
 * appropriate. Falls back to Level 2 if suggestion is unknown — same
 * middle-ground default the backend uses.
 *
 * "See all" opens /library for the full 3-level grid.
 *
 * Iter-52.16 refinement (this pass): switched from "always Level 1"
 * to `suggested_level` from /api/starter-library so a Class 3 child
 * sees Level 2 passages, a Class 5 child sees Level 3, etc. Added
 * a "Picked for Class N" badge so parents/children understand why
 * these two cards were chosen.
 */

const LEVEL_COLOR = { 1: '#4A9B7F', 2: '#5B8DEF', 3: '#B85582' };

function LevelBadge({ level }) {
  return (
    <span
      data-testid={`preview-level-badge-${level}`}
      className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full text-white"
      style={{ backgroundColor: LEVEL_COLOR[level] || LEVEL_COLOR[1] }}
    >L{level}</span>
  );
}
function LangTag({ language }) {
  const isHi = language === 'hi';
  return (
    <span
      className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full text-white"
      style={{ backgroundColor: isHi ? '#B85582' : '#5B8DEF' }}
    >{isHi ? 'हिंदी' : 'ENG'}</span>
  );
}

function PreviewCard({ passage }) {
  const nav = useNavigate();
  const preview = (passage.text || '').split('\n')[0].slice(0, 90);
  return (
    <button
      data-testid={`starter-passage-${passage.passage_id}`}
      onClick={() => nav(`/read/${passage.passage_id}`)}
      className="w-full text-left rounded-2xl bg-white p-4 shadow-md border-2 border-white hover:border-[#FF6B1A] transition-colors flex flex-col gap-2"
    >
      <div className="flex items-center gap-1.5">
        <LevelBadge level={passage.level}/>
        <LangTag language={passage.language}/>
      </div>
      <div className={`font-extrabold text-[#2B2140] text-base leading-tight ${passage.language === 'hi' ? 'font-hi' : ''}`}>
        {passage.title}
      </div>
      <div className={`text-sm text-slate-600 line-clamp-2 ${passage.language === 'hi' ? 'font-hi' : ''}`}>
        {preview}{preview.length >= 90 ? '…' : ''}
      </div>
    </button>
  );
}

/**
 * Turn a raw child.sub_level ('class_3', 'year_2', 'class_10', or
 * null) into a friendly human label for the "Picked for …" badge.
 * If we can't parse it we return null so the caller renders a
 * plain "Picked for you" fallback. Iter-52.17 — bilingual: `en`
 * emits "Class 3", `hi` emits "कक्षा 3".
 */
function classLabelForChild(child, lang = 'en') {
  if (!child) return null;
  const sl = (child.sub_level || '').trim().toLowerCase();
  if (sl.startsWith('class_')) {
    const n = parseInt(sl.replace('class_', ''), 10);
    if (Number.isFinite(n) && n > 0) return lang === 'hi' ? `कक्षा ${n}` : `Class ${n}`;
  }
  if (sl.startsWith('year_')) {
    const n = parseInt(sl.replace('year_', ''), 10);
    if (Number.isFinite(n) && n > 0) return lang === 'hi' ? `साल ${n}` : `Year ${n}`;
  }
  return null;
}

export default function StarterLibrarySection({ childId, homeLang = 'en' }) {
  const nav = useNavigate();
  const { activeChild } = useApp();
  const [passages, setPassages] = useState([]);
  const [suggestedLevel, setSuggestedLevel] = useState(null);
  const [loading, setLoading] = useState(true);

  // Iter-52.17 — Home toggle. All UI copy in this card flips between
  // English and Hindi based on the parent's saved preference. The
  // passage titles themselves stay in their original language (they
  // are lesson content, not UI chrome).
  const isHi = homeLang === 'hi';
  const T = HOME_STRINGS[isHi ? 'hi' : 'en'];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const q = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
    api.get(`/starter-library${q}`).then(({data}) => {
      if (cancelled) return;
      setPassages(data.passages || []);
      setSuggestedLevel(data.suggested_level ?? null);
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [childId]);

  // Effective preview level: backend suggestion → L2 fallback so the
  // card is NEVER empty. Same fallback the backend uses when class is
  // missing, kept in sync so the badge reads correctly.
  const effectiveLevel = suggestedLevel ?? 2;

  // Preview = first English + first Hindi at the effective level. If
  // either language is missing at that level, we fall back to any
  // level-effective passage; ultimately anything Level ≤ effective
  // so the section is never blank on Home.
  const preview = useMemo(() => {
    const atLevel = passages.filter(p => p.level === effectiveLevel);
    const en = atLevel.find(p => p.language === 'en');
    const hi = atLevel.find(p => p.language === 'hi');
    const chosen = [en, hi].filter(Boolean);
    if (chosen.length < 2) {
      for (const p of atLevel) {
        if (chosen.length >= 2) break;
        if (!chosen.includes(p)) chosen.push(p);
      }
    }
    if (chosen.length < 2) {
      // Absolute fallback: sweep from any level so Home isn't empty.
      for (const p of passages) {
        if (chosen.length >= 2) break;
        if (!chosen.includes(p)) chosen.push(p);
      }
    }
    return chosen;
  }, [passages, effectiveLevel]);

  const classLabel = classLabelForChild(activeChild, isHi ? 'hi' : 'en');
  const badgeText = classLabel
    ? `${T.starter_picked_prefix} ${classLabel}`
    : T.starter_picked_for_you;

  return (
    <section
      data-testid="starter-library-section"
      data-suggested-level={effectiveLevel}
      className="rounded-3xl p-5 md:p-6 mb-6 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #E97140 0%, #FF6B1A 100%)',
        boxShadow: '0 14px 36px -14px rgba(233,113,64,0.55)',
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        <div className="w-9 h-9 rounded-2xl bg-white/25 flex items-center justify-center">
          <BookOpen size={18} strokeWidth={2.8} className="text-white"/>
        </div>
        <div>
          <h2 className={`font-extrabold text-white text-lg leading-tight ${isHi ? 'font-hi' : ''}`}>{T.starter_heading}</h2>
          <p className={`text-xs font-bold text-white/90 ${isHi ? 'font-hi' : ''}`}>{T.starter_subtitle}</p>
        </div>
      </div>

      <div
        data-testid="starter-picked-for-badge"
        className={`mt-3 inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider text-[#B45309] bg-white/95 px-3 py-1 rounded-full shadow-sm ${isHi ? 'font-hi normal-case tracking-normal' : ''}`}
      >
        <Sparkles size={12} strokeWidth={3}/> {badgeText} · {T.starter_level_label} {effectiveLevel}
      </div>

      <div className="mt-4">
        {loading ? (
          <div className={`text-sm font-bold text-white/90 ${isHi ? 'font-hi' : ''}`}>{T.starter_loading}</div>
        ) : preview.length === 0 ? (
          <div className={`text-sm font-bold text-white/90 italic ${isHi ? 'font-hi' : ''}`}>{T.starter_empty}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {preview.map(p => <PreviewCard key={p.passage_id} passage={p}/>)}
          </div>
        )}
      </div>

      <button
        data-testid="starter-see-all-btn"
        onClick={() => nav('/library')}
        className={`mt-4 w-full sm:w-auto bg-white text-[#B45309] font-extrabold px-5 py-2.5 rounded-full inline-flex items-center justify-center gap-1.5 shadow-md hover:shadow-lg transition-shadow ${isHi ? 'font-hi' : ''}`}
      >
        {T.starter_see_all} <ChevronRight size={16} strokeWidth={3}/>
      </button>
    </section>
  );
}
